import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicFeeAgreementAPI } from "../lib/api";
import { downloadPdfRequest } from "../lib/pdfNavigation";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SignaturePad from "../components/SignaturePad";

const resolveApiErrorMessage = (error, fallback) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const formatAmount = (value, currency = "EUR") => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return amount.toLocaleString("de-DE", { style: "currency", currency });
};

const formatFactor = (value) => {
  const factor = Number(value);
  if (!Number.isFinite(factor)) return String(value ?? "");
  return `${factor.toLocaleString("de-DE", { maximumFractionDigits: 6 })}-fach`;
};

const formatDate = (value) => {
  if (!value) return "";
  const raw = String(value).slice(0, 10).split("-");
  return raw.length === 3 ? `${raw[2]}.${raw[1]}.${raw[0]}` : String(value);
};

/**
 * Unterschriftsseite zur Honorarvereinbarung nach § 2 GOÄ.
 *
 * § 2 Abs. 2 GOÄ gibt den Inhalt abschliessend vor und verbietet weitere
 * Erklaerungen. Diese Seite zeigt deshalb ausschliesslich die Leistungen mit
 * Steigerungssatz und Betrag, den Erstattungshinweis und das Unterschriftsfeld
 * - keine AGB, keine Einwilligung, keine Datenschutzerklaerung.
 */
export default function PublicFeeAgreement() {
  const { code } = useParams();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [signature, setSignature] = useState("");
  const [payerName, setPayerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await publicFeeAgreementAPI.get(code);
      setAgreement(response.data);
    } catch (error) {
      setAgreement(null);
      setLoadError(resolveApiErrorMessage(error, "Die Vereinbarung konnte nicht geladen werden."));
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDownload = async () => {
    try {
      await downloadPdfRequest(() => publicFeeAgreementAPI.downloadPdf(code), `${agreement?.document_number || "Honorarvereinbarung"}.pdf`);
    } catch (error) {
      setSubmitError(resolveApiErrorMessage(error, "Der Abdruck konnte nicht heruntergeladen werden."));
    }
  };

  const handleSubmit = async () => {
    if (!signature) {
      setSubmitError("Bitte im Feld unterschreiben.");
      return;
    }
    if (!payerName.trim()) {
      setSubmitError("Bitte den Namen eintragen.");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await publicFeeAgreementAPI.sign(code, {
        signature,
        payer_name: payerName.trim(),
      });
      setAgreement(response.data);
    } catch (error) {
      setSubmitError(resolveApiErrorMessage(error, "Die Unterschrift konnte nicht gespeichert werden."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6">
        <p className="text-sm text-[#86868B]">Vereinbarung wird geladen...</p>
      </div>
    );
  }

  if (loadError || !agreement) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-6">
        <Card className="w-full max-w-lg rounded-2xl border-0 shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm text-[#1D1D1F]">{loadError || "Die Vereinbarung wurde nicht gefunden."}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const alreadySigned = agreement.status === "signed" || Boolean(agreement.payer_signed_at);

  return (
    <div className="min-h-screen bg-[#F5F5F7] p-4 sm:p-8">
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <Card className="rounded-2xl border-0 shadow-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl font-semibold text-[#1D1D1F]">Honorarvereinbarung nach § 2 GOÄ</CardTitle>
            <p className="text-sm text-[#86868B]">
              {agreement.document_number} · {formatDate(agreement.agreement_date)}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-[#86868B]">Behandelnde Ärztin / behandelnder Arzt</p>
                <p className="text-sm font-medium text-[#1D1D1F]">{agreement.practitioner_name}</p>
                <p className="text-sm whitespace-pre-line text-[#636366]">{agreement.issuer_name}</p>
                <p className="text-sm whitespace-pre-line text-[#636366]">{agreement.issuer_address}</p>
              </div>
              <div>
                <p className="text-xs text-[#86868B]">
                  {agreement.payer_is_patient ? "Patientin / Patient und zahlungspflichtige Person" : "Zahlungspflichtige Person"}
                </p>
                <p className="text-sm font-medium text-[#1D1D1F]">{agreement.payer_name}</p>
                <p className="text-sm whitespace-pre-line text-[#636366]">{agreement.payer_address}</p>
                {!agreement.payer_is_patient && (
                  <p className="mt-1 text-sm text-[#636366]">Behandelt wird: {agreement.patient_name}</p>
                )}
              </div>
            </div>

            <p className="text-sm text-[#1D1D1F]">
              Für die folgenden Leistungen wird abweichend von der Gebührenordnung für Ärzte diese Vergütung
              vereinbart. Die Leistungen sind für den {formatDate(agreement.service_date_expected)} vorgesehen.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[520px] text-left text-sm">
                <thead>
                  <tr className="bg-[#F5F5F7] text-xs text-[#636366]">
                    <th className="px-3 py-2 font-medium">Nummer</th>
                    <th className="px-3 py-2 font-medium">Bezeichnung der Leistung</th>
                    <th className="px-3 py-2 font-medium text-center">Steigerungssatz</th>
                    <th className="px-3 py-2 font-medium text-right">{agreement.tax_mode ? "Vereinbarter Betrag" : agreement.calculation_version === 2 ? "Vereinbartes Nettohonorar" : "Vereinbarter Betrag"}</th>
                  </tr>
                </thead>
                <tbody>
                  {(agreement.items || []).map((item) => (
                    <tr key={item.position_no} className="border-b border-[#F0F0F2] align-top">
                      <td className="px-3 py-2 font-medium text-[#1D1D1F]">{item.code}</td>
                      <td className="px-3 py-2 text-[#1D1D1F]">{item.description}</td>
                      <td className="px-3 py-2 text-center text-[#636366]">{formatFactor(item.factor)}</td>
                      <td className="px-3 py-2 text-right font-medium text-[#1D1D1F]">
                        <span className="block">{formatAmount(item.agreed_amount, agreement.currency)}{agreement.tax_mode === "vat" ? " netto" : ""}</span>
                        {agreement.tax_mode === "vat" && <><span className="block text-xs font-normal text-[#636366]">zzgl. {Number(agreement.tax_rate).toLocaleString("de-DE")} % USt: {formatAmount(item.tax_amount, agreement.currency)}</span><span className="block">inkl. USt: {formatAmount(item.amount_gross, agreement.currency)}</span></>}
                        {agreement.tax_mode && agreement.tax_mode !== "vat" && <span className="block text-xs font-normal text-[#636366]">ohne Umsatzsteuer</span>}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="px-3 py-2" colSpan={3} />
                    <td className="px-3 py-2 text-right font-semibold text-[#1D1D1F]">
                      {formatAmount(agreement.total_with_tax ?? agreement.total_agreed, agreement.currency)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-sm font-medium text-[#1D1D1F]">
              {agreement.restitution_notice}
            </p>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-0 shadow-sm">
          <CardContent className="space-y-4 p-6">
            {agreement.calculation_version === 2 ? (<div className="space-y-3"><p className="text-sm">Diese Vereinbarung wird nach persönlicher Arztabsprache von beiden Parteien eigenhändig auf Papier abgeschlossen. Bitte wenden Sie sich an Ihre Praxis. Eine gezeichnete Browserunterschrift gibt diesen Vorgang nicht frei.</p><Button onClick={handleDownload}>Dokument herunterladen</Button></div>) : alreadySigned ? (
              <div className="space-y-4">
                <p className="text-sm font-medium text-[#1D1D1F]">
                  {agreement.status === "signed" ? "Die beidseitig unterschriebene Vereinbarung ist archiviert." : "Deine gezeichnete Unterschrift wurde erfasst. Für die rechtswirksame Honorarvereinbarung ist zusätzlich ein beidseitig unterschriebenes Papierdokument oder eine qualifizierte elektronische Signatur (QES) erforderlich."}
                </p>
                <p className="text-sm text-[#636366]">
                  Bitte den Abdruck der Vereinbarung herunterladen und aufbewahren.
                </p>
                <Button type="button" className="btn-primary" onClick={handleDownload}>
                  Abdruck herunterladen
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">Die gezeichnete Unterschrift dokumentiert deine Bestätigung, ersetzt aber keine qualifizierte elektronische Signatur. Für eine wirksame §-2-GOÄ-Vereinbarung muss das Dokument anschließend von beiden Parteien auf Papier unterschrieben und in der Praxis archiviert werden.</p>
                <div className="space-y-1.5">
                  <Label htmlFor="fee-agreement-name" className="text-sm text-[#1D1D1F]">
                    Name der zahlungspflichtigen Person
                  </Label>
                  <Input
                    id="fee-agreement-name"
                    className="input-base"
                    value={payerName}
                    onChange={(event) => setPayerName(event.target.value)}
                    placeholder={agreement.payer_name}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-sm text-[#1D1D1F]">Unterschrift</Label>
                  <SignaturePad value={signature} onChange={setSignature} disabled={submitting} />
                </div>
                {submitError ? <p className="text-sm text-red-600">{submitError}</p> : null}
                <Button type="button" className="btn-primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Wird gespeichert..." : "Unterschrift erfassen"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
